/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { decodeJwt } from 'jose';
import { autorun } from 'mobx';

import { authService, userService } from 'services';
import config from 'utils/config';
import { DUMMY_ORGANIZATION_ID, OIDC_TYPE } from 'utils/constants';
import { userStore } from 'stores';

import BaseOrganizationStore from './OrganizationStore.common';

const ELEVATED_ACCESS_PERMISSION = 'admin:*';
const JWT_PERMISSIONS_CLAIM = 'permissions';

class OrganizationStore extends BaseOrganizationStore {
  constructor() {
    super();

    autorun((reaction) => {
      if (userStore.isAuthenticated) {
        this.setHasElevatedOrganizationPermissions();
        reaction.dispose();
      }
    });
  }

  async setHasElevatedOrganizationPermissions() {
    try {
      const permissions = await this.#getUserPermissions();
      this.hasElevatedOrganizationPermissions = permissions?.includes(ELEVATED_ACCESS_PERMISSION);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  }

  /**
   * Gets the permissions assigned to the user depending on the OIDC type equivalent to the Java identity-sdk.
   *
   * @returns {Promise<string[]>}
   */
  async #getUserPermissions() {
    if ([OIDC_TYPE.GENERIC, OIDC_TYPE.MICROSOFT].includes(config?.oAuth2?.type)) {
      return await userService.getUserPermissions(config?.oAuth2?.token?.audience);
    } else if (authService.token && config?.oAuth2?.type === OIDC_TYPE.KEYCLOAK) {
      return decodeJwt(authService.token)?.[JWT_PERMISSIONS_CLAIM]?.[config?.oAuth2?.token?.audience];
    }

    return [];
  }

  /**
   * Compares the current organization with a given ID and switches
   * the context if they are not identical.
   * @return {void}
   */
  get currentOrganizationId() {
    // @ts-expect-error TS2322
    return DUMMY_ORGANIZATION_ID;
  }

  /**
   * True, if the given hostname is allowed to send invites
   * @return {boolean}
   */
  isAllowedHostnameForInvites() {
    return true;
  }

  /**
   * Compares the current organization with a given ID and switches
   * the context if they are not identical.
   * @return {void}
   */
  compareAndSwitchOrganization = () => {};

  /**
   * True, if the user should see the cluster creation journey, or the Deploy/Run modal directly
   * @return {boolean}
   */
  shouldSeeClusterCreationJourney() {
    return false;
  }

  /**
   * Everyone on self-managed has access to play tab as a client id and secret are required to use it.
   * @returns {boolean}
   */
  get hasPlayC8Access() {
    return true;
  }
}

export default new OrganizationStore();
